import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import {
  Store, Package, ShoppingBag, DollarSign,
  Play, Pause, Ban, Trash2, Eye, EyeOff,
  CheckCircle, Search
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminMarketplacePage = () => {
  const [stats, setStats] = useState(null);
  const [stores, setStores] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      const [statsRes, storesRes] = await Promise.all([
        fetch(`${API_URL}/api/admin/marketplace/stats`, { credentials: 'include' }),
        fetch(`${API_URL}/api/admin/marketplace/stores${filter !== 'all' ? `?status=${filter}` : ''}`, {
          credentials: 'include'
        })
      ]);

      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }

      if (storesRes.ok) {
        const data = await storesRes.json();
        setStores(data.stores || []);
      }
    } catch (error) {
      toast.error('Failed to load marketplace data');
    } finally {
      setLoading(false);
    }
  };

  const handleStoreAction = async (storeId, action, reason = null) => {
    setActionLoading({ ...actionLoading, [storeId]: action });
    
    try {
      const endpoint = `${API_URL}/api/admin/marketplace/stores/${storeId}/${action}`;
      const res = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action, reason })
      });

      if (res.ok) {
        toast.success(`Store ${action} successful`);
        loadData();
      } else {
        toast.error(`Failed to ${action} store`);
      }
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setActionLoading({ ...actionLoading, [storeId]: null });
    }
  };

  const toggleVisibility = async (storeId, currentVisibility) => {
    setActionLoading({ ...actionLoading, [storeId]: 'visibility' });
    
    try {
      const res = await fetch(`${API_URL}/api/admin/marketplace/stores/${storeId}/visibility?visible=${!currentVisibility}`, {
        method: 'POST',
        credentials: 'include'
      });

      if (res.ok) {
        toast.success(`Store visibility changed`);
        loadData();
      } else {
        toast.error('Failed to change visibility');
      }
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setActionLoading({ ...actionLoading, [storeId]: null });
    }
  };

  const confirmAction = (storeId, action, storeName) => {
    const messages = {
      pause: `Pause "${storeName}"? This will temporarily stop all sales.`,
      suspend: `Suspend "${storeName}"? This will hide the store and prevent all sales.`,
      delete: `Delete "${storeName}"? This action will remove the store from the marketplace.`
    };

    if (window.confirm(messages[action])) {
      const reason = prompt(`Reason for ${action}:`);
      if (reason) {
        handleStoreAction(storeId, action, reason);
      }
    }
  };

  const filteredStores = stores.filter(store =>
    store.store_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    store.owner_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
      paused: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
      suspended: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
      deleted: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
      pending_approval: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="p-6">
          <div className="text-center">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Store className="w-6 h-6 text-blue-600" />
            Marketplace Management
          </h1>
          <p className="text-sm text-muted-foreground">
            Control all marketplace stores, products, and activities
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-4">
          <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Stores</p><p className="text-2xl font-bold">{stats?.stores?.total || 0}</p></div><Store className="w-8 h-8 text-blue-500" /></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Active</p><p className="text-2xl font-bold text-green-600">{stats?.stores?.active || 0}</p></div><CheckCircle className="w-8 h-8 text-green-500" /></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Paused</p><p className="text-2xl font-bold text-yellow-600">{stats?.stores?.paused || 0}</p></div><Pause className="w-8 h-8 text-yellow-500" /></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Suspended</p><p className="text-2xl font-bold text-red-600">{stats?.stores?.suspended || 0}</p></div><Ban className="w-8 h-8 text-red-500" /></div></CardContent></Card>
          <Card><CardContent className="p-6"><div className="flex items-center justify-between"><div><p className="text-sm text-muted-foreground">Total Orders</p><p className="text-2xl font-bold">{stats?.orders?.total || 0}</p></div><ShoppingBag className="w-8 h-8 text-purple-500" /></div></CardContent></Card>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>All Stores</CardTitle>
              <div className="flex gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input placeholder="Search stores..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9 w-64" />
                </div>
                <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')}>All</Button>
                <Button variant={filter === 'active' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('active')}>Active</Button>
                <Button variant={filter === 'paused' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('paused')}>Paused</Button>
                <Button variant={filter === 'suspended' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('suspended')}>Suspended</Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {filteredStores.length === 0 ? (
              <p className="text-center text-muted-foreground py-8">No stores found</p>
            ) : (
              <div className="space-y-3">
                {filteredStores.map(store => (
                  <Card key={store.id} className="border-2">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-4 flex-1">
                          <div className="bg-blue-100 dark:bg-blue-900 p-3 rounded-lg">
                            <Store className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold text-lg">{store.store_name}</h3>
                              <Badge className={getStatusColor(store.status)}>{store.status}</Badge>
                              {store.verified && <Badge variant="success">Verified</Badge>}
                              {!store.visible && <Badge variant="secondary" className="flex items-center gap-1"><EyeOff className="w-3 h-3" />Hidden</Badge>}
                            </div>
                            <p className="text-sm text-muted-foreground mb-2">Owner: {store.owner_name} • Type: {store.store_type}</p>
                            <div className="flex gap-4 text-sm">
                              <span className="flex items-center gap-1"><Package className="w-4 h-4" />{store.product_count || 0} products</span>
                              <span className="flex items-center gap-1"><ShoppingBag className="w-4 h-4" />{store.order_count || 0} orders</span>
                              <span className="flex items-center gap-1"><DollarSign className="w-4 h-4" />{store.total_revenue || 0} revenue</span>
                            </div>
                            {(store.pause_reason || store.suspend_reason) && (
                              <div className="mt-2 bg-yellow-50 dark:bg-yellow-950 border border-yellow-200 dark:border-yellow-800 rounded p-2">
                                <p className="text-xs text-yellow-800 dark:text-yellow-200"><strong>Reason:</strong> {store.pause_reason || store.suspend_reason}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {store.status === 'active' && (<><Button variant="outline" size="sm" onClick={() => confirmAction(store.id, 'pause', store.store_name)} disabled={actionLoading[store.id]}><Pause className="w-4 h-4 mr-1" />Pause</Button><Button variant="outline" size="sm" onClick={() => confirmAction(store.id, 'suspend', store.store_name)} disabled={actionLoading[store.id]} className="text-orange-600"><Ban className="w-4 h-4 mr-1" />Suspend</Button></>)}
                          {(store.status === 'paused' || store.status === 'suspended') && (<Button variant="default" size="sm" onClick={() => handleStoreAction(store.id, 'resume')} disabled={actionLoading[store.id]}><Play className="w-4 h-4 mr-1" />Resume</Button>)}
                          <Button variant="ghost" size="sm" onClick={() => toggleVisibility(store.id, store.visible)} disabled={actionLoading[store.id]} title={store.visible ? 'Hide from public' : 'Show to public'}>{store.visible ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</Button>
                          <Button variant="ghost" size="sm" onClick={() => confirmAction(store.id, 'delete', store.store_name)} disabled={actionLoading[store.id]} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
};
